import { useEffect, useRef, useState } from "react";
import { AlertTriangle, Check, Copy, Link2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

export const careersUrl = (job) => `${window.location.origin}/careers/${job.careers_page?.slug || job.id}`;
const MS = { copied: 4000, failed: 10000 }; // copied is a fallback cap; normally it ends with the toast
const TOAST_MS = 2500;
// Survives re-renders and remounts: copy id -> { status, until }.
const memo = new Map();

const withTimeout = (p, ms) => Promise.race([p, new Promise((_, rej) => setTimeout(() => rej(new Error("timeout")), ms))]);

function execCopy(text) {
  const ta = Object.assign(document.createElement("textarea"), { value: text, readOnly: true });
  ta.style.cssText = "position:fixed;top:0;left:0;opacity:0";
  document.body.appendChild(ta);
  ta.focus();
  ta.select();
  let ok = false;
  try { ok = document.execCommand("copy"); } catch { ok = false; }
  ta.remove();
  return ok;
}

// Async Clipboard API first (can reject or hang inside iframes without permission), then execCommand.
async function writeClipboard(text) {
  try { if (navigator.clipboard?.writeText) { await withTimeout(navigator.clipboard.writeText(text), 1500); return true; } } catch { /* fall back */ }
  return execCopy(text);
}

function useCopyState(id) {
  const read = () => { const m = memo.get(id); return m && m.until > Date.now() ? m.status : "idle"; };
  const [status, setStatus] = useState(read);
  const [tick, setTick] = useState(0);
  useEffect(() => {
    if (status === "idle") return undefined;
    const t = setTimeout(() => setStatus("idle"), Math.max(0, (memo.get(id)?.until || 0) - Date.now()));
    return () => clearTimeout(t);
  }, [status, id, tick]);
  const set = (s) => { memo.set(id, { status: s, until: Date.now() + (MS[s] || 0) }); setStatus(s); setTick((n) => n + 1); };
  return [status, set];
}

export function CopyButton({ text, label = "Copy link", iconOnly = false, icon: Icon = Copy, testId = "copy-btn", size = "default", variant = "outline", className }) {
  const [status, setStatus] = useCopyState(`${testId}|${text}`);
  const input = useRef(null);
  const copy = async () => {
    setStatus("copied"); // optimistic: the button changes on the click itself
    const ok = await writeClipboard(text);
    setStatus(ok ? "copied" : "failed"); // restarts the 2.5s hold from the moment the toast shows
    // The button stays "Copied ✓" exactly as long as the toast is on screen, then both go together.
    if (ok) toast.success("Link copied to clipboard", { description: text, duration: TOAST_MS, onAutoClose: () => setStatus("idle"), onDismiss: () => setStatus("idle") });
    else toast.error("Copy failed. The link is selected below, press Ctrl/Cmd+C to copy it.");
  };
  useEffect(() => { if (status === "failed") input.current?.select(); }, [status]);
  const copied = status === "copied";
  const failed = status === "failed";
  const shown = copied ? "Copied ✓" : failed ? "Copy failed. Select the link" : label;
  return (
    <span className="inline-flex flex-wrap items-center gap-2">
      <Button variant={variant} size={size} onClick={copy} data-testid={testId} data-copied={copied ? "true" : "false"} data-copy-status={status}
        aria-label={copied ? "Copied" : failed ? "Copy failed. Select the link" : label}
        className={cn(className, copied && "!border-[#2F6B45] !bg-[#E7EFE9] !text-[#2F6B45] ring-2 ring-[#2F6B45]/25 hover:!bg-[#E7EFE9]",
          failed && "!border-[#9A6700] !bg-[#F6EEDC] !text-[#9A6700] hover:!bg-[#F6EEDC]")}>
        {copied ? <Check size={16} strokeWidth={2.5} aria-hidden="true" data-testid={`${testId}-check`} /> : failed ? <AlertTriangle size={16} aria-hidden="true" /> : <Icon size={16} aria-hidden="true" />}
        {(status !== "idle" || !iconOnly) && <span data-testid={`${testId}-label`} className={status !== "idle" ? "font-semibold" : undefined}>{shown}</span>}
      </Button>
      <span className="sr-only" role="status" aria-live="polite" data-testid={`${testId}-live`}>{copied ? "Link copied to clipboard" : failed ? "Copy failed. The link is selected in the text box." : ""}</span>
      {failed && (
        <input ref={input} readOnly value={text} onFocus={(e) => e.target.select()} aria-label="Careers page link" data-testid={`${testId}-fallback-input`}
          className="h-8 w-72 max-w-full rounded-[5px] border border-[#9A6700] bg-white px-2 text-xs text-ink outline-none focus:ring-2 focus:ring-[#9A6700]/30" />
      )}
    </span>
  );
}

export const CopyLinkButton = ({ url, label = "Copy careers page link", testId = "copy-careers-link", ...rest }) => (
  <CopyButton text={url} label={label} icon={Link2} testId={testId} {...rest} />
);
